import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  CreateRoleBody,
  PaginatedResult,
  RoleDetail,
  RoleListItem,
  RolesListQuery,
  UpdateRoleApiResponse,
  UpdateRoleBody,
} from '@/types/roles';

/**
 * Typed wrappers around the 6 `/api/roles` endpoints from Plan 07-01.
 *
 * Every wrapper:
 *  - Throws an `ApiError` (via `toApiError`) on non-2xx so consumers can
 *    `instanceof`-switch on `e.status`.
 *  - Returns the typed response object on success.
 */

function buildListUrl(q: RolesListQuery): string {
  const params = new URLSearchParams();
  if (q.q !== undefined && q.q !== '') params.set('q', q.q);
  if (q.status !== undefined) params.set('status', q.status);
  if (q.page !== undefined) params.set('page', String(q.page));
  if (q.pageSize !== undefined) params.set('pageSize', String(q.pageSize));
  const qs = params.toString();
  return qs.length > 0 ? `/api/roles?${qs}` : '/api/roles';
}

export async function listRolesRequest(
  query: RolesListQuery,
): Promise<PaginatedResult<RoleListItem>> {
  try {
    const { data } = await api.get<PaginatedResult<RoleListItem>>(buildListUrl(query));
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getRoleRequest(id: number): Promise<RoleDetail> {
  try {
    const { data } = await api.get<RoleDetail>(`/api/roles/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function createRoleRequest(body: CreateRoleBody): Promise<RoleDetail> {
  try {
    const { data } = await api.post<RoleDetail>('/api/roles', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function updateRoleRequest(
  id: number,
  body: UpdateRoleBody,
): Promise<UpdateRoleApiResponse> {
  try {
    const { data } = await api.put<UpdateRoleApiResponse>(`/api/roles/${String(id)}`, body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function deactivateRoleRequest(
  id: number,
  reassignToRoleId?: number | null,
): Promise<void> {
  try {
    const url =
      reassignToRoleId !== undefined && reassignToRoleId !== null
        ? `/api/roles/${String(id)}?reassignToRoleId=${String(reassignToRoleId)}`
        : `/api/roles/${String(id)}`;
    await api.delete(url);
  } catch (e) {
    throw toApiError(e);
  }
}

export async function reactivateRoleRequest(id: number): Promise<void> {
  try {
    await api.post(`/api/roles/${String(id)}/reactivate`);
  } catch (e) {
    throw toApiError(e);
  }
}
